import * as moment from "moment";

import { LinkObject } from "@teamprevent/common/model";
import { ServerModelWithDateStrings } from "@teamprevent/common/utils";
import { MedicalTypeId } from "@teamprevent/common/healthSurveillance/models";
import { Link, TaskModel } from "@teamprevent/common/task/taskActions/models";
import { ImmunisationForCase } from "@teamprevent/common/immunisation/models";
import { OhCaseBaseModel } from "@teamprevent/common/ohCase/model";

import { CreateTaskDto, parseTaskModel } from "../../task/model";

import { OhCaseDocumentApi } from "./ohCaseDocumentApi";

import { Location } from "redux-little-router";

export type CaseStatus = "Open" | "Closed" | "Cancelled";

export interface OhCaseListModel extends OhCaseBaseModel {
    readonly id: string;
    readonly caseId: string;
    readonly customerName: string;
    readonly created: moment.Moment | undefined;
    readonly lastUpdated: moment.Moment | undefined;
    readonly caseStatus: CaseStatus;
    readonly employeeRecordId: string;
    readonly caseType: string;
    readonly medicalTypes: MedicalTypeId[];
    readonly matchedReferralId: string | null;
    readonly employeeFirstName: string;
    readonly employeeLastName: string;
    readonly documents: OhCaseDocument[];
    readonly genericCaseType?: LinkObject;
}

export interface OhCaseDocument {
    id: string;
    name: string;
    documentType: string;
    dateCreated: moment.Moment | undefined;
    completedBy?: Link;
    visibleTo?: keyof typeof OhCaseDocumentVisibilityOptions;
}

export interface OhCaseDetailModel extends OhCaseListModel {
    readonly id: string;
    readonly customerName: string;
    readonly caseStatus: CaseStatus;
    readonly employeeRecordId: string;
    readonly jobTitle: string;
    readonly sex: string;
    readonly dateOfBirth: moment.Moment | undefined;
    readonly personalEmailAddress: string;
    readonly mobileNumber: string;
    readonly customerNotes: string;
    readonly criticalNotes: string;
    readonly tasks: TaskModel[];
    readonly associatedManagers: LinkObject[];
    readonly associatedHrUsers: LinkObject[];
    readonly immunisations: ImmunisationForCase[];
    readonly linkedOhCases: LinkedOhCase[];
    readonly tasksCompletedInCurrentSession: boolean;
    readonly totallyConfidential: boolean;
    readonly isSelfReferral: boolean;
    readonly medicalTypes: MedicalTypeId[];
    readonly deactivatedMedicalTypes: MedicalTypeId[];
    readonly workGroups: LinkObject[];
    readonly isQuestionnaireOnly: boolean;
    readonly origin: string;
    readonly secondaryCaseStatus: string;
    readonly additionalImmunisations: ImmunisationForCase[];
    readonly ppqOutcome: LinkObject | null;
    readonly employeeSecondaryReference: string;
    readonly purchaseOrderNumber: string;
}

export interface UpdateAssociatedManagersPayload {
    customerId: string;
    ohCaseId: string;
    associatedManagers: LinkObject[];
}

export interface UpdateAssociatedHrUsersPayload {
    customerId: string;
    ohCaseId: string;
    associatedHrUsers: LinkObject[];
}

export interface UpdatePurchaseOrderNumberPayload {
    customerId: string;
    ohCaseId: string;
    purchaseOrderNumber: string;
}

export interface CreateOhCaseDocumentPayload {
    ohCaseId: string;
    document: OhCaseDocument;
}

export interface UpdateOhCaseDocumentPayload {
    ohCaseId: string;
    document: OhCaseDocument;
}

export interface LoadOhCaseDocumentsPayload {
    ohCaseId: string;
    documents: OhCaseDocument[];
}

export interface DeleteOhCaseDocumentPayload {
    ohCaseId: string;
    ohCaseDocumentId: string;
}

export interface ActionTaskSuccessPayload {
    ohCaseId: string;
    tasks: TaskModel[];
}

export interface CreateTasksSuccessPayload {
    ohCaseId: string;
    tasks: TaskModel[];
}

export interface SetCaseConfidentialitySuccessPayload {
    ohCaseId: string;
    totallyConfidential: boolean;
    tasks: TaskModel[];
}

/**
 * A temporary type used to help with the transition from `OhCase` to `OhCaseDetailModel`.
 * Prefer `OhCaseDetailModel` but use this type if you wish to use the `OhCaseDetailModel`
 * but there is other referenced code that is still using `OhCase` type or this `PartialOhCaseDetailModel` type
 * and thus preventing the use of `OhCaseDetailModel`.
 */
export type PartialOhCaseDetailModel = Partial<Omit<OhCaseDetailModel, "id">> & { id: string };

/**
 * A temporary type used to help with the transition from `OhCase` to `OhCaseListModel`.
 * Prefer `OhCaseListModel` but use this type if you wish to use the `OhCaseListModel`
 * but there is other referenced code that is still using `OhCase` type or this `PartialOhCaseListModel` type
 * and thus preventing the use of `OhCaseListModel`.
 */
export type PartialOhCaseListModel = Partial<Omit<OhCaseListModel, "id">> & { id: string };

/**
 * @deprecated Something should really be either a list or a detail, not both. This is a legacy type we wish to remove. Replace with one of the following:
 * - `OhCaseDetailModel` if you need the detail properties
 * - `OhCaseListModel` if you need just the list properties
 * - `PartialOhCaseListModel` if you want to use the `OhCaseListModel` properties but referenced code is still using `OhCase` type
 * - `PartialOhCaseDetailModel` if you want to use the `OhCaseDetailModel` properties but referenced code is still using `OhCase` type
 * - `OhCaseInRedux` if you just want oh case as it comes from redux store
 */
export type OhCase = (OhCaseDetailModel | PartialOhCaseDetailModel) & (OhCaseListModel | PartialOhCaseListModel);
type PartialOhCaseModelInRedux = PartialOhCaseDetailModel & { documents?: OhCaseDocument[] };
type OhCaseListModelInRedux = OhCaseListModel & { listUpdated: moment.Moment };
type OhCaseDetailModelInRedux = OhCaseDetailModel & { listUpdated: moment.Moment; detailUpdated: moment.Moment };
export type OhCaseInRedux = PartialOhCaseModelInRedux | OhCaseListModelInRedux | OhCaseDetailModelInRedux;

export interface OhCaseCreateModel {
    id: string;
    caseId: string;
    caseType: string;
    customerName: string;
    caseStatus: CaseStatus;
    employeeName: string;
    jobTitle: string;
    sex: string;
    dateOfBirth: moment.Moment;
    personalEmailAddress: string;
    mobileNumber: string;
    customerNotes: string;
    criticalNotes: string;
    openTasks: string;
    completedTasks: string;
}

export interface CreatePhysiotherapyReferralCaseResult {
    physiotherapyReferralCase: OhCaseDetailModel;
    linkedCase: OhCaseDetailModel | null;
}

export interface ImmunisationCaseCreateModel {
    immunisationTypeIds: string[];
    dueDate: moment.Moment;
    linkedOhCaseId?: string;
}

export interface CreateImmunisationCaseResult {
    immunisationCase: OhCaseDetailModel;
    linkedCase: OhCaseDetailModel | null;
}

export interface OhCaseState {
    /* eslint-disable deprecation/deprecation */
    /* Once all references of state.ohCases can handle `OhCaseDetailModel | OhCaseListModel` then the
        type of this property can become `OhCaseDetailModel | OhCaseListModel` too and the `OhCase` type can be removed */
    readonly ohCases: (OhCase & { documents?: OhCaseDocument[]; listUpdated?: moment.Moment; detailUpdated?: moment.Moment })[];
    /* eslint-enable deprecation/deprecation */
}

export interface LinkedOhCase {
    id: string;
    caseType: string;
    immsClear?: boolean;
    caseId?: number;
    evidenceOfCovid19VaccinationStatusMandatory?: boolean;
    covidVaccinationEvidenceSharingConsentOnPpq?: boolean;
    covidVaccinationEvidenceSharingConsentOnImmunisationReview?: boolean;
}

export type AppState = OhCaseState & { router: Location; loading: any };

export function parseOhCaseModel(model: ServerModelWithDateStrings<OhCaseDetailModel>): OhCaseDetailModel {
    return {
        ...model,
        created: model.created ? moment(model.created) : undefined,
        dateOfBirth: model.dateOfBirth ? moment(model.dateOfBirth) : undefined,
        lastUpdated: model.lastUpdated ? moment(model.lastUpdated) : undefined,
        tasks: model.tasks && model.tasks.map(parseTaskModel),
        immunisations: model.immunisations && model.immunisations.map(parseImmunisationForCase),
        additionalImmunisations: model.additionalImmunisations?.map(a => ({
            ...a,
            dateCompleted: a.dateCompleted ? moment(a.dateCompleted) : undefined
        })),
        documents: model.documents?.map(OhCaseDocumentApi.parseModel) ?? []
    };
}

export function parseOhCaseModelWithDocuments(
    model: ServerModelWithDateStrings<OhCaseDetailModel & { documents: OhCaseDocument[] }>
): OhCaseDetailModel & { documents: OhCaseDocument[] } {
    return {
        ...parseOhCaseModel(model),
        documents: model.documents.map(OhCaseDocumentApi.parseModel)
    };
}

export function parseImmunisationForCase(i: ServerModelWithDateStrings<ImmunisationForCase> | ImmunisationForCase) {
    return { ...i, dateCompleted: i.dateCompleted ? moment(i.dateCompleted) : undefined };
}

const isCompletedOrCancelled = (task: TaskModel) => task.completed || task.cancelled;

export const sortTasks = (tasks: TaskModel[]) => {
    return [...tasks].sort(Sort);
};

const Sort = <T extends TaskModel>(a: T, b: T) => {
    if (isCompletedOrCancelled(a) && !isCompletedOrCancelled(b)) {
        return 1;
    }

    if (!isCompletedOrCancelled(a) && isCompletedOrCancelled(b)) {
        return -1;
    }

    const aDate = (isCompletedOrCancelled(a) && a.completedDate) ? a.completedDate : a.dateCreated;
    const bDate = (isCompletedOrCancelled(b) && b.completedDate) ? b.completedDate : b.dateCreated;

    return -aDate.diff(bDate);
};

export interface ActionTaskModel {
    followUpTask: CreateTaskDto;
    notes: string;
}

export enum OhCaseDocumentVisibilityOptions {
    Admin = "Admin",
    Employee = "Employee",
    Manager = "Manager",
    EmployeeAndManager = "EmployeeAndManager"
}
